import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Loader2, ImageIcon, Mail } from "lucide-react";
import api, { API } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Button } from "@/components/ui/button";

function ActivadorCard({ d }) {
  return (
    <div
      data-testid={`activador-thumb-${d.id}`}
      className="rounded-md border border-zinc-400 bg-white overflow-hidden flex flex-col"
    >
      <div className="aspect-[3/2] bg-zinc-100 overflow-hidden flex items-center justify-center">
        {d.has_template ? (
          <img
            src={`${API}/diplomas/${d.id}/template?t=${d.id}`}
            alt={d.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center text-zinc-500">
            <ImageIcon className="h-8 w-8 mx-auto mb-1" />
            <span className="text-[10px] uppercase tracking-widest">Sin plantilla</span>
          </div>
        )}
      </div>
      <div className="p-3 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#5EA529] font-semibold">
          Activador
        </p>
        <h3 className="font-mono font-bold text-sm text-black mt-1 leading-tight">
          {d.name}
        </h3>
        {d.required_activations > 0 && (
          <p className="text-[11px] text-zinc-600 mt-1 font-mono">
            {d.required_activations} activaciones
          </p>
        )}
      </div>
    </div>
  );
}

export default function ActivadoresLanding() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/diplomas")
      .then(({ data }) => setItems((data || []).filter((d) => d.type === "activator")))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#D6D3D1] text-zinc-900">
      <PublicNav />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <section className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-black/60 font-semibold mb-2">
            Radio Club Ochentero · Reconocimiento al activador
          </p>
          <h1 className="font-mono text-3xl sm:text-4xl font-bold tracking-tighter text-black leading-none inline-block border-b-[8px] border-[#5EA529] pb-1">
            Diplomas de activadores
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-700 leading-relaxed">
            Reconocimiento a las estaciones que salen a activar. Cada activación (log
            asignado al operador) suma un punto hacia el siguiente nivel.
          </p>
        </section>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#5EA529]" />
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <p
                data-testid="no-activadores-message"
                className="text-center text-zinc-600 py-10"
              >
                Aún no hay diplomas de activador publicados. Vuelve pronto.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-[#5EA529]" />
                  <h2 className="font-mono text-lg uppercase tracking-[0.25em] font-bold text-black">
                    Niveles disponibles
                  </h2>
                  <span className="text-xs font-mono text-zinc-600">
                    ({items.length})
                  </span>
                </div>
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(0, 1fr))`,
                  }}
                >
                  {items
                    .slice()
                    .sort(
                      (a, b) =>
                        (a.required_activations || 0) - (b.required_activations || 0)
                    )
                    .map((d) => (
                      <ActivadorCard key={d.id} d={d} />
                    ))}
                </div>
              </>
            )}

            <div className="mt-12 rounded-md border border-[#5EA529] bg-white p-6 text-center max-w-2xl mx-auto">
              <h3 className="font-mono text-xl font-bold text-black mb-2">
                ¿Quieres conseguir tu diploma de activador?
              </h3>
              <p className="text-sm text-zinc-700 mb-5">
                Contacta con el club, coordina tu activación y sube al ranking.
              </p>
              <Link to="/contacto">
                <Button
                  data-testid="cta-activador-contacto"
                  className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm h-12 px-8"
                >
                  <Mail className="h-4 w-4 mr-2" /> Ponte en contacto
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
